/**
 * SegmentRoller — client-side segmentation for the web recorder (ADR-020
 * Brick 2, web port of the iOS roller).
 *
 * Rolls the live recording into a series of COMPLETE webm/opus segment files
 * (~3.5 min each, cut at a client-heard silence), uploading each to
 * POST /notes/:id/segments as it rolls. The server transcribes each segment
 * on arrival with chained context, so the full transcript converges while
 * the consult is still happening.
 *
 * Safety model (identical to iOS):
 *  - Runs on a CLONE of the mic stream via its own MediaRecorder per segment
 *    — the main recorder, its chunk buffer, and the progressive upload are
 *    untouched. Stopping a segment recorder yields a complete, headers-intact
 *    webm (the web never had iOS's unfinalised-container problem).
 *  - Segments are transcription ACCELERATORS. Any failure here (mic clone
 *    unsupported, upload rejects, network gone) just deactivates the roller;
 *    the server assembly never becomes ready and the note transcribes through
 *    the normal path. Nothing can be lost or slowed.
 *  - Server-driven enablement: the settings-cascade `segment_roller` flag.
 *    The roller starts buffering from t=0 unconditionally (so an enabled
 *    roller never misses the opening words) and cancels itself within the
 *    first seconds if the flag comes back anything but true — no segment has
 *    rolled by then, so a disabled roller costs zero network.
 *  - Pause is honoured (CLA-1552). Because the roller records its own clone,
 *    pausing the main recorder does nothing to it — the store must call
 *    `pause()`/`resume()` here in lockstep, or a paused consult (patient out
 *    of the room, a private call) is captured and transcribed into the note.
 *    While paused: the segment recorder is paused, the roll clock stands
 *    still (paused time never counts toward a cut), and a roll already in
 *    flight does not start the next segment until resume.
 *
 * Whether a ready assembly becomes the note (fast path) or a comparison row
 * (shadow validation) is the server's `segment_live_mode` decision — the
 * client just ships segments either way.
 *
 * Backup capture (CLA-1552, John's decision (a)): because the roller has its
 * own recorder on its own clone, it keeps capturing when the MAIN recorder
 * dies under a mic that is still live. The clinician is told recording
 * continues on backup, so the store has to know whether that is true —
 * `isCapturing()` answers, and `onCapturingChange` reports every change.
 */
import { api } from '@/api/client'

const MIN_CUT_MS = 195_000
const HARD_CUT_MS = 240_000
const SILENCE_RMS = 0.005
const SILENCE_WINDOW_MS = 700
const POLL_MS = 500
const MAX_UPLOAD_ATTEMPTS = 5
const FINISH_DRAIN_MS = 15_000

export class SegmentRoller {
  private noteId: number
  private stream: MediaStream | null = null
  private recorder: MediaRecorder | null = null
  private chunks: Blob[] = []
  private idx = 0
  private active = true
  private failed = false
  /** The clinician has paused the consult (CLA-1552). */
  private paused = false
  /** When the current pause began, for excluding it from the roll clock. */
  private pausedAt: number | null = null
  /**
   * A roll finished while paused: the next segment recorder is owed, and is
   * started by `resume()` — never while paused.
   */
  private nextSegmentOwed = false
  private uploaded = 0
  private uploadChain: Promise<void> = Promise.resolve()
  private cancelled = false
  private segStartedAt = 0
  private quietSince: number | null = null
  private pollHandle: ReturnType<typeof setInterval> | null = null
  private audioContext: AudioContext | null = null
  private analyser: AnalyserNode | null = null
  private levelBuf: Uint8Array<ArrayBuffer> | null = null
  /** The server said `segment_roller: true`. Until then, not a backup. */
  private enabled = false
  private onCapturingChange: ((capturing: boolean) => void) | null
  private reportedCapturing = false

  constructor(noteId: number, opts: { onCapturingChange?: (capturing: boolean) => void } = {}) {
    this.noteId = noteId
    this.onCapturingChange = opts.onCapturingChange ?? null
  }

  /**
   * Is this roller recording the consult right now — enabled by the server,
   * not paused, not torn down, on a mic that is delivering audio? This is
   * what "still recording on backup" means, so it must never be true when
   * the roller is doing nothing useful: before the flag answers, while
   * paused, or on a muted or ended track (a clone shares its source's fate).
   */
  isCapturing(): boolean {
    if (!this.active || !this.enabled || this.paused || !this.stream) return false
    const track = this.stream.getAudioTracks?.()?.[0]
    if (!track) return false
    return track.readyState !== 'ended' && !track.muted
  }

  /** Start rolling from the given LIVE mic stream (cloned internally). */
  begin(sourceStream: MediaStream): void {
    try {
      this.stream = sourceStream.clone()
      this.watchTrack()
      this.setupLevelMonitor()
      this.startSegmentRecorder()
      this.pollHandle = setInterval(() => this.tick(), POLL_MS)
    } catch {
      // Clone/recorder unsupported — roller inert, normal path carries the note.
      this.deactivate()
    }

    // Server-driven flag: cancel within the first seconds when not enabled.
    // (Buffering from t=0 until then costs nothing — no roll for minutes.)
    void this.checkFlag()
  }

  /**
   * Roll + upload the tail segment (`final=1`), then drain the upload chain
   * (bounded — a stuck network must not stall the stop path). Returns whether
   * every segment landed; false just means the server falls through.
   */
  async finish(): Promise<boolean> {
    if (!this.active) return false
    await this.roll(true)
    this.deactivate()

    const drained = await Promise.race([
      this.uploadChain.then(() => true),
      new Promise<boolean>((resolve) => setTimeout(() => resolve(false), FINISH_DRAIN_MS)),
    ])
    return drained && !this.failed && this.uploaded === this.idx
  }

  /**
   * Discard without uploading the tail (cancelled / silent-mic recording).
   * Segments still queued are dropped too, and an upload in flight is not
   * retried: a discarded recording's segments must not reach the note after
   * the server has been asked to forget them (CLA-1880).
   */
  cancel(): void {
    this.cancelled = true
    this.deactivate()
  }

  /** Resolves once the upload in flight, if any, has finished (see `cancel`). */
  drained(): Promise<void> {
    return this.uploadChain
  }

  /**
   * The clinician paused the consult: capture nothing until `resume()`
   * (CLA-1552). Idempotent. A recorder that refuses to pause deactivates the
   * roller instead — dropping an accelerator is always safe, recording a
   * paused consult never is.
   */
  pause(): void {
    if (this.paused) return
    this.paused = true
    this.pausedAt = Date.now()
    this.quietSince = null
    this.reportCapturing()
    if (!this.active) return
    const recorder = this.recorder
    if (recorder && recorder.state === 'recording') {
      try {
        recorder.pause()
      } catch {
        this.deactivate()
      }
    }
  }

  /** Continue the same segment chain after a pause. Idempotent. */
  resume(): void {
    if (!this.paused) return
    this.paused = false
    // Paused time never counts toward a cut: shift the segment's start.
    if (this.pausedAt !== null) this.segStartedAt += Date.now() - this.pausedAt
    this.pausedAt = null
    this.quietSince = null
    this.reportCapturing()
    if (!this.active) return
    if (this.nextSegmentOwed) {
      this.nextSegmentOwed = false
      try {
        this.startSegmentRecorder()
      } catch {
        this.deactivate()
      }
      return
    }
    const recorder = this.recorder
    if (recorder && recorder.state === 'paused') {
      try {
        recorder.resume()
      } catch {
        this.deactivate()
      }
    }
  }

  // ── internals ─────────────────────────────────────────────────────────

  private async checkFlag(): Promise<void> {
    try {
      const settings = await api.get<{ segment_roller?: boolean | null }>('/settings')
      if (settings.segment_roller !== true) {
        this.deactivate()
        return
      }
      this.enabled = true
      this.reportCapturing()
    } catch {
      this.deactivate()
    }
  }

  private startSegmentRecorder(): void {
    if (!this.stream) return
    const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
      ? 'audio/webm;codecs=opus'
      : 'audio/webm'
    const chunks: Blob[] = []
    const recorder = new MediaRecorder(this.stream, { mimeType })
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunks.push(e.data)
    }
    recorder.start(1000)
    this.recorder = recorder
    this.chunks = chunks
    this.segStartedAt = Date.now()
    this.quietSince = null
  }

  private tick(): void {
    if (!this.active || !this.recorder || this.paused) return
    const elapsed = Date.now() - this.segStartedAt

    const rms = this.currentRMS()
    if (rms !== null && rms < SILENCE_RMS) {
      this.quietSince = this.quietSince ?? Date.now()
    } else {
      this.quietSince = null
    }
    const quietFor = this.quietSince ? Date.now() - this.quietSince : 0

    if ((elapsed >= MIN_CUT_MS && quietFor >= SILENCE_WINDOW_MS) || elapsed >= HARD_CUT_MS) {
      void this.roll(false)
    }
  }

  private async roll(final: boolean): Promise<void> {
    const recorder = this.recorder
    if (!recorder) {
      // Finished while paused between segments (CLA-1552): the last segment
      // already shipped, so the tail is empty — but the server still needs
      // the `final` marker to know the chain is complete.
      if (final && this.nextSegmentOwed) {
        this.nextSegmentOwed = false
        this.enqueueUpload(this.idx++, true, new Blob([], { type: 'audio/webm' }))
      }
      return
    }
    this.recorder = null

    const chunks = this.chunks
    await new Promise<void>((resolve) => {
      recorder.onstop = () => resolve()
      if (recorder.state !== 'inactive') recorder.stop()
      else resolve()
    })
    recorder.ondataavailable = null

    const idx = this.idx++
    const blob = new Blob(chunks, { type: 'audio/webm' })

    if (blob.size > 0 || final) this.enqueueUpload(idx, final, blob)
    if (!final && this.active) {
      // The clinician paused while this segment was finalising: starting the
      // next recorder now would capture the pause (CLA-1552). `resume()`
      // starts it.
      if (this.paused) this.nextSegmentOwed = true
      else this.startSegmentRecorder()
    }
  }

  private enqueueUpload(idx: number, final: boolean, blob: Blob): void {
    this.uploadChain = this.uploadChain.then(async () => {
      // Frozen complete blob: retries are byte-identical; server upserts on
      // (note, idx).
      for (let attempt = 1; attempt <= MAX_UPLOAD_ATTEMPTS; attempt++) {
        if (this.cancelled) return
        try {
          const form = new FormData()
          form.append('idx', String(idx))
          form.append('final', final ? '1' : '0')
          form.append('segment', blob, `seg-${idx}.webm`)
          await api.postForm(`/notes/${this.noteId}/segments`, form)
          this.uploaded++
          return
        } catch (e) {
          const status = (e as { status?: number })?.status
          if (status === 400 || status === 404 || status === 422) {
            // Server validation reject — terminal; assembly falls through.
            this.failed = true
            return
          }
          await new Promise((r) => setTimeout(r, Math.min(attempt * 2000, 8000)))
        }
      }
      this.failed = true
    })
  }

  /** The clone's track going quiet or away changes whether we are a backup. */
  private watchTrack(): void {
    const track = this.stream?.getAudioTracks?.()?.[0]
    if (!track || typeof track.addEventListener !== 'function') return
    const onChange = () => this.reportCapturing()
    for (const type of ['mute', 'unmute', 'ended'] as const) track.addEventListener(type, onChange)
  }

  private reportCapturing(): void {
    const capturing = this.isCapturing()
    if (capturing === this.reportedCapturing) return
    this.reportedCapturing = capturing
    this.onCapturingChange?.(capturing)
  }

  private setupLevelMonitor(): void {
    try {
      if (!this.stream) return
      const Ctx = window.AudioContext ?? (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
      if (!Ctx) return
      this.audioContext = new Ctx()
      const source = this.audioContext.createMediaStreamSource(this.stream)
      this.analyser = this.audioContext.createAnalyser()
      this.analyser.fftSize = 512
      source.connect(this.analyser)
      this.levelBuf = new Uint8Array(new ArrayBuffer(this.analyser.fftSize))
    } catch {
      // No level data → silence-snapping degrades to hard cuts only. Fine.
    }
  }

  /** RMS in 0..1, or null when no analyser is available. */
  private currentRMS(): number | null {
    if (!this.analyser || !this.levelBuf) return null
    this.analyser.getByteTimeDomainData(this.levelBuf)
    let sum = 0
    for (let i = 0; i < this.levelBuf.length; i++) {
      const v = (this.levelBuf[i] - 128) / 128
      sum += v * v
    }
    return Math.sqrt(sum / this.levelBuf.length)
  }

  private deactivate(): void {
    this.active = false
    this.nextSegmentOwed = false
    if (this.pollHandle) {
      clearInterval(this.pollHandle)
      this.pollHandle = null
    }
    if (this.recorder && this.recorder.state !== 'inactive') {
      try {
        this.recorder.stop()
      } catch {
        // already stopped
      }
    }
    this.recorder = null
    void this.audioContext?.close().catch(() => undefined)
    this.audioContext = null
    this.analyser = null
    this.stream?.getTracks().forEach((t) => t.stop())
    this.stream = null
    this.reportCapturing()
  }
}
